import { AppThunk } from '../../types';
import { AxiosError } from 'axios';
import { createCdBuilderPresentationItems } from '../../../components/cdBuilder/utilities/createCdBuilderPresentationItems';
import { createValidationResult } from '../../../services/Validation/createValidationResult';
import { getCDItemsAsync, saveCDItemsAsync } from '../../../services/RestItemsService';
import { getOwnerDetails } from '../../../components/cdBuilder/getOwnerDetails';
import {
  setCdItemsAction,
  setCdItemsErrorAction,
  setCdItemsFetchStatusAction,
  setRefiCondition,
  setUpdateCdItemsErrorAction,
  setUpdateCdItemsFetchStatusAction,
  setValidationAction,
} from './reducer';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
} from 'types/rest/IFetchStatus';
import { generateCDViewItemCollection } from '../../../ui-toolkit/cdItems/data/generateViewItems';
import { ICDV3ViewItem, ISalesTaxViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { IOwnerDetailsServiceItem } from '../../../ui-toolkit/cdItems/service/ServiceInterfaces';
import { getRefiDiscountByState } from '../../../ui-toolkit/cdItems/service/ServiceMethods';
import { IParty } from '../../../ui-toolkit/orders/interfaces/IParty';
import { IOrderDetailsData } from '../../../ui-toolkit/orders/interfaces/IOrderDetailsData';

export const initCdItems =
  (ownerId: string, items: ICDV3ViewItem[]): AppThunk =>
  async (dispatch, getState) => {
    const savedItems = getState().order.cdBuilder[ownerId];
    if (!savedItems) {
      dispatch(
        setCdItemsAction({
          orderId: ownerId,
          value: {
            items: generateCDViewItemCollection(items),
            deletedItems: [],
            changesSaved: false,
          },
        })
      );
    }
  };

export const loadItemsData =
  (ownerId: string, parties: IParty[], details: IOwnerDetailsServiceItem): AppThunk =>
  async (dispatch) => {
    dispatch(setCdItemsFetchStatusAction(createDefaultFetch()));
    dispatch(setCdItemsErrorAction(null));
    try {
      dispatch(setCdItemsFetchStatusAction(createFetchInProgress()));
      const items = await getCDItemsAsync(ownerId);
      const presentationItems = createCdBuilderPresentationItems(items.items, details, parties, ownerId);
      dispatch(setItemsAndDetails(ownerId, presentationItems, true, false));
      dispatch(setCdItemsFetchStatusAction(createFetchedSuccess()));
    } catch (e: any) {
      dispatch(setCdItemsErrorAction(getErrorMessage(e)));
      dispatch(setCdItemsFetchStatusAction(createFetchedWithError()));
    }
  };

const getErrorMessage = (e: AxiosError): Error => {
  let errorMessage = '';
  if (e.response) {
    switch (e.response.status) {
      case 403:
        errorMessage = 'You do not have permission to edit this order/quote.';
        break;
      case 404:
        errorMessage = 'We could not load this order/quote.';
        break;
      default:
        errorMessage = `Error ${e.response.status}: ${e.response.statusText}`;
        break;
    }
  } else if (e.request) {
    errorMessage = e.request;
  } else if (e.message) {
    errorMessage = e.message;
  }
  return new Error(errorMessage);
};

export const setItemsAndDetails =
  (orderId: string, items: ICDV3ViewItem[], clearDeletedItems: boolean, changesSaved: boolean): AppThunk =>
  async (dispatch, getState) => {
    const deletedItems = getState().order.cdBuilder.cdItems[orderId].deletedItems;
    dispatch(
      setCdItemsAction({
        orderId,
        value: {
          items: generateCDViewItemCollection(items),
          deletedItems: clearDeletedItems ? [] : deletedItems,
          changesSaved: changesSaved,
        },
      })
    );
  };

export const addCdItem =
  (ownerId: string, item: ICDV3ViewItem): AppThunk =>
  (dispatch, getState) => {
    const oldItems = getState().order.cdBuilder.cdItems[ownerId].items;
    const items = [...oldItems.asArray, item];
    dispatch(setItemsAndDetails(ownerId, items, false, false));
  };

export const replaceCdItem =
  (ownerId: string, newItem: ICDV3ViewItem): AppThunk =>
  (dispatch, getState) => {
    const oldItems = getState().order.cdBuilder.cdItems[ownerId].items;
    const items = oldItems.asArray.map((item) => (item.id === newItem.id ? newItem : item));
    dispatch(setItemsAndDetails(ownerId, items, false, false));
  };

export const removeItem =
  (ownerId: string, item: ICDV3ViewItem): AppThunk =>
  (dispatch, getState) => {
    let deletedItems = getState().order.cdBuilder.cdItems[ownerId].deletedItems;

    deletedItems = [...deletedItems, item];

    // When we delete an item, delete all children. Children have a parent_id equal to this item.
    //  Currently only sales tax can potentially have a parent_id.
    let items = getState().order.cdBuilder.cdItems[ownerId].items.asArray;
    const childrenItems = items.filter((t) => (t as ISalesTaxViewItem).parent_id === item.id);
    items = items.filter((value) => value.id !== item.id);
    items = items.filter((value) => childrenItems.indexOf(value) < 0);
    dispatch(
      setCdItemsAction({
        orderId: ownerId,
        value: {
          items: generateCDViewItemCollection(items),
          deletedItems: deletedItems,
          changesSaved: false,
        },
      })
    );
  };

export const validateAndSave =
  (ownerId: string, orderDetails: IOrderDetailsData): AppThunk =>
  async (dispatch, getState) => {
    const deletedItems = getState().order.cdBuilder.cdItems[ownerId].deletedItems;
    const items = getState().order.cdBuilder.cdItems[ownerId].items;
    const changesSaved = getState().order.cdBuilder.cdItems[ownerId].changesSaved;
    const validationResult = createValidationResult(items, orderDetails);
    const dirtyItems = items.asArray.filter((item: ICDV3ViewItem) => item.dirty);
    const deletedSavedItems = deletedItems.filter((item: ICDV3ViewItem) => !item.new);
    const hasItemsToSave = dirtyItems.length > 0 || deletedSavedItems.length > 0;
    dispatch(setValidationAction({ orderId: ownerId, value: validationResult }));

    dispatch(setUpdateCdItemsFetchStatusAction(createDefaultFetch()));
    dispatch(setUpdateCdItemsErrorAction(null));
    try {
      const savedItems =
        validationResult.valid && hasItemsToSave && !changesSaved
          ? await saveCDItemsAsync(ownerId, dirtyItems, deletedSavedItems)
          : await getCDItemsAsync(ownerId);
      const presentationItems = createCdBuilderPresentationItems(
        savedItems.items,
        getOwnerDetails(orderDetails),
        orderDetails.parties,
        ownerId
      );
      dispatch(setItemsAndDetails(ownerId, presentationItems, true, true));
      dispatch(setUpdateCdItemsFetchStatusAction(createFetchedSuccess()));
    } catch (e: any) {
      dispatch(setUpdateCdItemsFetchStatusAction(createFetchedWithError()));
      dispatch(setUpdateCdItemsErrorAction(getErrorMessage(e)));
    }
  };

export const setRefiDiscountByState =
  (state: string, fips: string, clientName: string): AppThunk =>
  async (dispatch) => {
    const response = await getRefiDiscountByState(state, fips, clientName);
    dispatch(setRefiCondition(response instanceof Error ? null : (response.reason || '').toLowerCase()));
  };
